import {useCallback, useMemo, useState} from 'react';
import {FabricObject} from 'fabric';

export const FILTER_KINDS = ['table', 'shape', 'text', 'wall'] as const;

const naturalCompare = (a: string, b: string): number => {
    const aParts = a.split(/(\d+)/);
    const bParts = b.split(/(\d+)/);

    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
        const aPart = aParts[i] ?? '';
        const bPart = bParts[i] ?? '';

        const aNum = parseInt(aPart, 10);
        const bNum = parseInt(bPart, 10);

        if (!isNaN(aNum) && !isNaN(bNum)) {
            if (aNum !== bNum) return aNum - bNum;
        } else {
            const cmp = aPart.localeCompare(bPart, undefined, {sensitivity: 'base'});
            if (cmp !== 0) return cmp;
        }
    }
    return 0;
};

const getElementName = (element: FabricObject): string => {
    const item = element.floorPlanItem;
    const table = item?.table;
    const elementData = item?.element;
    const kind = elementData?.kind;
    const shape = table?.shape ?? (elementData?.kind === 'shape' ? elementData.shape : undefined);
    const canvas = elementData?.canvas as any;
    const label = canvas?.label;

    if (table) return table.resource?.name ?? "Table";
    if (kind === "text") return canvas?.text ?? "Text";
    if (kind === "wall") return label || "Wall";
    return label || (shape ? shape.charAt(0).toUpperCase() + shape.slice(1) : "Rect");
};

const getElementKind = (el: FabricObject): string => {
    if (el.floorPlanItem?.table) return 'table';
    return el.floorPlanItem?.element?.kind ?? '';
};

export const useListPanelFilters = (elements: FabricObject[]) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [activeFilters, setActiveFilters] = useState<Set<string>>(() => new Set(FILTER_KINDS));
    const [toolbarOpen, setToolbarOpen] = useState(false);

    const toggleToolbar = useCallback(() => {
        setToolbarOpen(prev => {
            if (prev) {
                setSearchQuery('');
                setSortDirection('asc');
                setActiveFilters(new Set(FILTER_KINDS));
            }
            return !prev;
        });
    }, []);

    const toggleFilter = useCallback((kind: string) => {
        setActiveFilters(prev => {
            const next = new Set(prev);
            if (next.has(kind)) next.delete(kind);
            else next.add(kind);
            return next;
        });
    }, []);

    const sortMultiplier = sortDirection === 'asc' ? 1 : -1;
    const lowerQuery = searchQuery.toLowerCase();

    const tables = useMemo(() => {
        if (!activeFilters.has('table')) return [];
        return elements
            .filter(el => {
                if (!el.floorPlanItem?.table) return false;
                if (lowerQuery && !getElementName(el).toLowerCase().includes(lowerQuery)) return false;
                return true;
            })
            .sort((a, b) => sortMultiplier * naturalCompare(getElementName(a), getElementName(b)));
    }, [elements, lowerQuery, activeFilters, sortMultiplier]);

    const otherElements = useMemo(() => {
        return elements
            .filter(el => {
                if (el.floorPlanItem?.table || !el.floorPlanItem?.element) return false;
                if (!activeFilters.has(getElementKind(el))) return false;
                if (lowerQuery && !getElementName(el).toLowerCase().includes(lowerQuery)) return false;
                return true;
            })
            .sort((a, b) => sortMultiplier * naturalCompare(getElementName(a), getElementName(b)));
    }, [elements, lowerQuery, activeFilters, sortMultiplier]);

    const filteredCount = tables.length + otherElements.length;

    return {
        searchQuery,
        setSearchQuery,
        sortDirection,
        setSortDirection,
        activeFilters,
        toolbarOpen,
        toggleToolbar,
        toggleFilter,
        tables,
        otherElements,
        filteredCount,
    };
};
